import { Component, type ErrorInfo, type ReactNode } from 'react'
import { report } from '@/lib/telemetry'
import { RecoveryScreen } from './RecoveryScreen'

interface Props {
  children: ReactNode
  /** Distinguishes a whole-app crash from one contained to a single screen. */
  scope: 'app' | 'route'
}

interface State {
  error: Error | null
}

/**
 * Catches render-phase errors so a bug in one component cannot blank the
 * entire app — the failure mode behind the reported "app suddenly resets".
 *
 * Route-scoped boundaries let the rest of the shell (navigation, header) keep
 * working while one screen is broken, so the user can still navigate away
 * instead of being stuck.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    report({
      level: 'error',
      event: `react.${this.props.scope}_crash`,
      message: error.message,
      stack: error.stack,
      context: { componentStack: info.componentStack?.slice(0, 1500) },
    })
  }

  private readonly clear = (): void => {
    this.setState({ error: null })
  }

  override render(): ReactNode {
    const { error } = this.state
    if (error) {
      return <RecoveryScreen error={error} scope={this.props.scope} onDismiss={this.clear} />
    }
    return this.props.children
  }
}
